if (!customElements.get('product-card-2')) {
  class ProductCard2 extends HTMLElement {
    static framingCache = new Map();
    connectedCallback() {
      this.image = this.querySelector('[data-card2-image]');
      this.colorLabel = this.querySelector('[data-card2-color-label]');
      this.normalizeFraming = this.normalizeFraming.bind(this);
      this.image?.addEventListener('load', this.normalizeFraming);
      this.normalizeFraming();
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.image?.removeEventListener('load', this.normalizeFraming);
    }

    onClick(event) {
      const dot = event.target.closest('[data-card2-dot]');
      if (dot) {
        event.preventDefault();
        this.updateImage(dot);
        this.setActiveDot(dot);
        return;
      }

      const swatch = event.target.closest('[data-card2-swatch]');
      if (swatch) {
        event.preventDefault();
        this.updateImage(swatch);
        this.setSelectedSwatch(swatch);
      }
    }

    updateImage(source) {
      if (!this.image) return;
      const { src, srcset, width, height, alt } = source.dataset;
      if (!src) return;

      this.image.src = src;
      if (srcset) this.image.srcset = srcset;
      if (width) this.image.width = width;
      if (height) this.image.height = height;
      if (typeof alt === 'string') this.image.alt = alt;
    }

    normalizeFraming() {
      const image = this.image;
      if (!image?.complete || !image.naturalWidth) return;
      image.style.removeProperty('--card2-image-scale');
      image.style.removeProperty('--card2-image-offset');
      image.classList.remove('product-card-2__image--normalized');
      const media = image.closest('.product-card-2__media');
      media?.style.removeProperty('--card2-image-background');
      const imageRatio = image.naturalWidth / image.naturalHeight;
      if (!media || imageRatio < 0.6 || imageRatio > 0.75) return;
      const source = image.currentSrc || image.src;
      if (ProductCard2.framingCache.has(source)) {
        this.applyFraming(ProductCard2.framingCache.get(source));
        return;
      }
      if (ProductCard2.framingCache.size >= 256) {
        ProductCard2.framingCache.delete(ProductCard2.framingCache.keys().next().value);
      }
      ProductCard2.framingCache.set(source, null);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 80;
        canvas.height = Math.round(canvas.width / imageRatio);
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
        const pixel = (x, y) => {
          const index = (y * canvas.width + x) * 4;
          const alpha = data[index + 3] / 255;
          return [0, 1, 2].map((channel) => Math.round(data[index + channel] * alpha + 255 * (1 - alpha)));
        };
        const corners = [
          pixel(1, 1), pixel(canvas.width - 2, 1),
          pixel(1, canvas.height - 2), pixel(canvas.width - 2, canvas.height - 2)
        ];
        const background = [0, 1, 2].map((channel) =>
          [...corners.map((color) => color[channel])].sort((a, b) => a - b)[1]
        );
        if (Math.min(...background) < 225 || Math.max(...background) - Math.min(...background) > 20) return;
        if (corners.some((color) => color.some((value, channel) => Math.abs(value - background[channel]) > 24))) return;
        const rows = [];
        for (let y = 0; y < canvas.height; y += 1) {
          let left = canvas.width;
          let right = -1;
          let count = 0;
          for (let x = 2; x < canvas.width - 2; x += 1) {
            const color = pixel(x, y);
            if (color.some((value, channel) => Math.abs(value - background[channel]) > 36)) {
              left = Math.min(left, x);
              right = x;
              count += 1;
            }
          }
          rows.push({ left, right, count, width: right - left + 1 });
        }
        const top = rows.findIndex((row) => row.count >= 3);
        let bottom = rows.length - 1;
        while (bottom > top && rows[bottom].count < 3) bottom -= 1;
        if (top < 0 || top / canvas.height > 0.065 || bottom / canvas.height < 0.94) return;
        const subjectHeight = (bottom - top + 1) / canvas.height;
        if (subjectHeight < 0.9) return;
        const bandWidth = (start, end) => {
          const widths = rows.slice(top + Math.round((bottom - top) * start), top + Math.round((bottom - top) * end))
            .filter((row) => row.count >= 3).map((row) => row.width).sort((a, b) => a - b);
          return widths[Math.floor(widths.length / 2)] || 0;
        };
        const headWidth = bandWidth(0.05, 0.18);
        const bodyWidth = bandWidth(0.3, 0.7);
        if (bodyWidth < canvas.width * 0.5 || bodyWidth > canvas.width * 0.92 || headWidth > bodyWidth * 0.75) return;
        const scale = Math.min(1.08, 0.97 / subjectHeight);
        const offset = 0.03 - ((top / canvas.height - 0.5) * scale + 0.5);
        const framing = { scale, offset, background };
        ProductCard2.framingCache.set(source, framing);
        this.applyFraming(framing);
      } catch {
        return;
      }
    }

    applyFraming(framing) {
      if (!framing) return;
      this.image.style.setProperty('--card2-image-scale', framing.scale.toFixed(4));
      this.image.style.setProperty('--card2-image-offset', (framing.offset * 100).toFixed(3) + '%');
      this.image.closest('.product-card-2__media').style.setProperty('--card2-image-background', 'rgb(' + framing.background.join(', ') + ')');
      this.image.classList.add('product-card-2__image--normalized');
    }

    setActiveDot(activeDot) {
      this.querySelectorAll('[data-card2-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', dot === activeDot);
      });
    }

    setSelectedSwatch(activeSwatch) {
      this.querySelectorAll('[data-card2-swatch]').forEach((swatch) => {
        const isSelected = swatch === activeSwatch;
        swatch.classList.toggle('is-selected', isSelected);
        swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      if (this.colorLabel && activeSwatch.dataset.name) {
        this.colorLabel.textContent = activeSwatch.dataset.name;
      }
    }
  }

  customElements.define('product-card-2', ProductCard2);
}
